import {
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
} from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import type { PropsWithChildren } from "react";

const runtime = window.APP_CONFIG ?? {
  environment: "local",
  apiBaseUrl: "/api",
  mockAuth: true,
  entraTenantId: "",
  entraClientId: "",
  entraApiScope: "",
};

const msal = runtime.mockAuth
  ? null
  : new PublicClientApplication({
      auth: {
        clientId: runtime.entraClientId,
        authority: `https://login.microsoftonline.com/${runtime.entraTenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: { cacheLocation: "sessionStorage" },
    });

if (msal) await msal.initialize();

export function AuthProvider({ children }: PropsWithChildren) {
  if (!msal) return children;
  return <MsalProvider instance={msal}>{children}</MsalProvider>;
}

export type AuthenticationState = {
  isAuthenticated: boolean;
  isBusy: boolean;
  accountEmail: string | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
};

const mockAuthentication: AuthenticationState = {
  isAuthenticated: true,
  isBusy: false,
  accountEmail: "local.admin@payangel.com",
  signIn: async () => undefined,
  signOut: async () => undefined,
};

export function useAuthentication(): AuthenticationState {
  if (runtime.mockAuth) return mockAuthentication;
  return useEntraAuthentication();
}

function useEntraAuthentication(): AuthenticationState {
  const { instance, accounts, inProgress } = useMsal();
  const account = accounts[0] ?? null;
  return {
    isAuthenticated: Boolean(account),
    isBusy: inProgress !== InteractionStatus.None,
    accountEmail: account?.username ?? null,
    signIn: async () => {
      await instance.loginPopup({
        scopes: [runtime.entraApiScope],
        prompt: "select_account",
      });
    },
    signOut: async () => {
      await instance.logoutPopup({
        account: account ?? undefined,
        postLogoutRedirectUri: window.location.origin,
      });
    },
  };
}

export function useAccessToken() {
  if (runtime.mockAuth) return async () => null;
  return useEntraAccessToken();
}

function useEntraAccessToken() {
  const { instance, accounts } = useMsal();
  return async () => {
    const account = accounts[0];
    if (!account) throw new Error("Sign in with Microsoft to continue");
    try {
      return (
        await instance.acquireTokenSilent({
          account,
          scopes: [runtime.entraApiScope],
        })
      ).accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return (
          await instance.acquireTokenPopup({
            account,
            scopes: [runtime.entraApiScope],
          })
        ).accessToken;
      }
      throw error;
    }
  };
}

export const runtimeConfig = runtime;
